import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "react-bootstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast, ToastContainer } from "react-toastify";
import FormHandler from "../../Common/Form/FormHandler/FormHandler";
import { ExecuteSopsConfig } from "./ExecuteSopsConfig";
import { useTranslation } from "react-i18next";
import {
  getData,
  postDataWithBody,
  putDataWithBody,
  getDataById,
} from "../../../../Services/Services";
 
import {
  getTaskConfigsURL,
  createTaskURL,
  findTaskById,
  getTaskListURL,
  updateTaskURL,
  SopTaskTableData,
  sopResourceList,
  sopGetIncident,
  SopUserData,
  CreateSopInstance,
  getAllIncidentsByCtegorySop,
  getAllSopByIncident,
} from "../../../../Services/EndPoints";
 
import Loader from "react-spinner-loader";
import { downloadFile } from "../../../../Components/UiComponents/utils";
import { PaperClipIcon } from "../../../../Components/IconComponents";
import { useKeycloak } from "@react-keycloak/web";
import ExecuteSopsExpandableTable from "./ExecuteSopsExpandableTable";

/**
 * ExecuteSopsForm Component
 * 
 * This component handles the execution of Standard Operating Procedures (SOPs).
 * It provides functionality to:
 * - Select incident categories and types
 * - Choose SOPs based on incident categories
 * - View and select tasks associated with SOPs
 * - Set task priorities
 * - Execute selected tasks as an SOP instance
 */
const ExecuteSopsForm = () => {
  // Translation hook for internationalization
  const { t } = useTranslation();
  
  // ========== STATE VARIABLES ==========
  
  // Task-related state
  const [userTaskData, setUserTaskData] = useState([]); // Array of tasks for the selected SOP
  const [rowCount, setRowCount] = useState(0); // Total number of tasks
  const [hasSelectedTasks, setHasSelectedTasks] = useState(false); // Track if any task is selected
  
  // Form and UI state
  const [isLoading, setIsLoading] = useState(false); // Loading state for async operations
  const [isSubmitting, setIsSubmitting] = useState(false); // Submitting state for form submission
  const [sopFormConfig, setSopFormConfig] = useState(ExecuteSopsConfig); // Form configuration
  const [activeTabIndex, setActiveTabIndex] = useState(0); // Active tab index
  const [Description, setDescription] = useState(false); // Description field visibility
  
  // Data state
  const [taskConfigs, setTaskConfigs] = useState([]); // Task configuration data
  const [viewTaskDetails, setViewTaskDetails] = useState(null); // Task details for editing
  const [availableRoles, setAvailableRoles] = useState([]); // Available SOP roles/options
  const [attachedFiles, setAttachedFiles] = useState([]); // Attached files for form
  const [sopResources, setSopResources] = useState([]); // SOP resource list
 
  // Routing and authentication
  const { state } = useLocation(); // Get state from navigation
  const navigate = useNavigate(); // Navigation hook
  const { keycloak } = useKeycloak(); // Keycloak authentication
 
  // Extract task ID and media file path from navigation state
  const taskId = state?.data?.id;
  const mediaFilePath = state?.data?.media_file_path;
 
  // ========== FORM SETUP ==========
  
  // Initialize react-hook-form with yup validation
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(),
    mode: "all",
  });
 
  // Watch for changes in form dropdown values
  const selectedSopId = watch("task_name"); // Selected SOP ID
  const selectedIncidentId = watch("incident_type_id"); // Selected incident type ID
  const incidentcatgeoryId = watch("indident_category"); // Selected incident category ID
 
 
  // ========== LIFECYCLE EFFECTS ==========
  
  /**
   * Initialize component on mount
   */
  useEffect(() => {
    setDescription(true);
  }, []);

  /**
   * Load initial data and populate form if editing
   */
  useEffect(() => {
    loadInitialData();
    fetchSopUserData();
  }, []);

  /**
   * Handle incident category changes
   * Clears dependent dropdowns and data when category changes
   */
  useEffect(() => {
    // Check if the incident category is empty (Select Category option)
    if (!incidentcatgeoryId) {
      // Clear incident types dropdown
      updateFormConfig(0, 1, []);
      // Clear SOP dropdown
      updateFormConfig(0, 2, []);
      // Clear selected values
      setValue("incident_type_id", "");
      setValue("task_name", "");
      // Clear the task list and resources
      setUserTaskData([]);
      setSopResources([]);
      // Clear description field
      setValue("description", "");
      // Clear assign_to_id field
      setValue("assign_to_id", "");
      // Reset available roles
      setAvailableRoles([]);
      return;
    }
 
    // If a valid category is selected, fetch the related data
    fetchIncidentTypes();
    fetchSopList();
    fetchViewData();
  }, [incidentcatgeoryId]);

  /**
   * Handle SOP selection changes
   * Updates description and assign_to_id fields based on selected SOP
   */
  useEffect(() => {
    if (selectedSopId && incidentcatgeoryId) {
      fetchTaskList();
      fetchSopResourceList();
 
      // Find the selected SOP
      const selectedSop = availableRoles.find(
        (sop) => sop.id === parseInt(selectedSopId)
      );
 
      if (selectedSop) {
        // Update the description field with the selected SOP's description
        setValue("description", selectedSop.description, {
          shouldValidate: true,
          shouldDirty: true,
        });
 
        // Generate timestamp for SOP instance name
        const now = new Date();
        const formattedDate = now
          .toLocaleDateString("en-GB")
          .replace(/\//g, ":"); // Format as DD:MM:YYYY
        const formattedTime = now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
 
        // Combine SOP name with date and time for unique instance name
        const nameValue = `${selectedSop.name}_${formattedDate} ${formattedTime}`;
 
        setValue("assign_to_id", nameValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    }
  }, [selectedSopId, availableRoles, incidentcatgeoryId]);

  /**
   * Handle incident type changes
   */
  useEffect(() => {
    if (selectedIncidentId) {
      fetchSopIncident();
    }
  }, [selectedIncidentId]);

  /**
   * Update button state based on task selection
   */
  useEffect(() => {
    const anyTaskSelected = userTaskData.some((task) => task.isSelected);
    setHasSelectedTasks(anyTaskSelected);
  }, [userTaskData]);
 
  // ========== API FUNCTIONS ==========

  /**
   * Fetch task list for selected SOP
   * @param {string} sopId - SOP ID to fetch tasks for
   */
  const fetchTaskList = async (sopId = selectedSopId) => {
    // Only proceed if we have both incident category and SOP selected
    if (!incidentcatgeoryId || !sopId) {
      setUserTaskData([]);
      setRowCount(0);
      return;
    }
   
    setIsLoading(true);
    try {
      const response = await getDataById(SopTaskTableData, sopId);
      // Sort tasks by creation date (newest first)
      const sortedTasks = response
        ? response.sort((a, b) => new Date(b.createdOn) - new Date(a.createdOn))
        : [];
 
      // Add isSelected property and default priority to each task
      const tasksWithSelection = sortedTasks.map((task) => ({
        ...task,
        isSelected: false, // Initially no tasks are selected
        priority: task.task_status_code || "Low", // Default priority if not provided
      }));
 
      setUserTaskData(tasksWithSelection);
      setRowCount(tasksWithSelection.length);
    } catch (error) {
      console.error("Error fetching task list:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch resource list for selected SOP
   * @param {string} sopId - SOP ID to fetch resources for
   */
  const fetchSopResourceList = async (sopId = selectedSopId) => {
    if (!incidentcatgeoryId || !sopId) {
      setSopResources([]);
      return;
    }
   
    setIsLoading(true);
    try {
      const response = await getDataById(sopResourceList, sopId);
      setSopResources(response || []);
    } catch (error) {
      console.error("Error fetching SOP resources:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch SOP incident data
   */
  const fetchSopIncident = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(sopGetIncident, selectedIncidentId);
    } catch (error) {
      console.error("Error fetching SOP incident:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetch SOP user data
   */
  const fetchSopUserData = async () => {
    setIsLoading(true);
    try {
      const response = await getDataById(SopUserData, selectedSopId);
    } catch (error) {
      console.error("Error fetching SOP user data:", error);
      toast.error(t("fetch_error_message"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load initial configuration data
   */
  const loadInitialData = async () => {
    setIsLoading(true);
    await fetchTaskConfigs();
    if (taskId) {
      populateFormForEdit(taskId);
    }
    setIsLoading(false);
  };

  /**
   * Fetch task configuration data including incident categories
   */
  const fetchTaskConfigs = async () => {
    try {
      const res = await postDataWithBody(
        getTaskConfigsURL,
        ["INCDNT_CAT"], // Request incident category configurations
        {}
      );
 
      setTaskConfigs(res); // Store raw config data
 
      // Process incident categories with hardcoded "Select Category" option
      const incidentCategories = [
        { label: "Select Category", value: "" }, // Default empty option
        ...res
          .filter(config => config.category_name === "INCDNT_CAT")
          .map(config => ({
            label: config.value,
            value: config.id,
          }))
      ];
 
      // Update the form config with incident categories
      const updatedConfig = [...sopFormConfig];
      if (updatedConfig[0] && updatedConfig[0].fields && updatedConfig[0].fields[0]) {
        updatedConfig[0].fields[0].options = incidentCategories;
        setSopFormConfig(updatedConfig);
      }
 
    } catch (error) {
      console.error("Failed to fetch task configs:", error);
    }
  };

  /**
   * Fetch incident types based on selected category
   */
  const fetchIncidentTypes = async () => {
    if (!incidentcatgeoryId) {
      updateFormConfig(0, 1, []);
      return;
    }
 
    try {
      const incidentTypes = await getData(getAllIncidentsByCtegorySop + incidentcatgeoryId);
      const formattedTypes =
        incidentTypes.length > 0
          ? incidentTypes.map((type) => ({
              label: type.name,
              value: type.id,
            }))
          : [{ label: "No options available", value: "" }];
 
      // Update incident types dropdown
      updateFormConfig(0, 1, formattedTypes);
 
      // Set default incident type if available
      if (incidentTypes.length > 0 && !getValues("incident_type_id")) {
        const defaultIncidentId = formattedTypes[0].value.toString();
        setValue("incident_type_id", defaultIncidentId);
      }
    } catch (error) {
      console.error("Failed to fetch incident types:", error);
      updateFormConfig(0, 1, [{ label: "No options available", value: "" }]);
    }
  };

  /**
   * Fetch SOP list based on selected incident category
   */
  const fetchSopList = async () => {
    if (!incidentcatgeoryId) {
      updateFormConfig(0, 2, []);
      setAvailableRoles([]);
      setValue("assign_to_id", "");
      return;
    }
 
    try {
      const sops = await getData(getAllSopByIncident + incidentcatgeoryId);
      setAvailableRoles(sops);
 
      const formattedSops =
        sops.length > 0
          ? sops.map((sop) => ({
              label: sop.name,
              value: sop.id,
            }))
          : [{ label: "No options available", value: "" }];
 
      // Update SOP dropdown
      updateFormConfig(0, 2, formattedSops);
 
      if (sops.length > 0) {
        // Set default SOP selection
        const defaultSopId = sops[0].id.toString();
        setValue("task_name", defaultSopId);
 
        // Set default description
        setValue("description", sops[0].description, {
          shouldValidate: true,
          shouldDirty: true,
        });
 
        // Generate default instance name with timestamp
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, ":");
        const formattedTime = now.toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        });
 
        const nameValue = `${sops[0].name}_${formattedDate} ${formattedTime}`;
        setValue("assign_to_id", nameValue, {
          shouldValidate: true,
          shouldDirty: true,
        });
 
        // Fetch related data for default SOP
        fetchTaskList(defaultSopId);
        fetchSopResourceList(defaultSopId);
      } else {
        setValue("assign_to_id", "", {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } catch (error) {
      console.error("Failed to fetch SOP list:", error);
      updateFormConfig(0, 2, [{ label: "No options available", value: "" }]);
      setAvailableRoles([]);
      setValue("assign_to_id", "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  // ========== UTILITY FUNCTIONS ==========

  /**
   * Update form configuration with new options
   * @param {number} configIndex - Index of config tab
   * @param {number} fieldIndex - Index of field within tab
   * @param {Array} options - New options to set
   */
  const updateFormConfig = (configIndex, fieldIndex, options) => {
    const updatedConfig = [...sopFormConfig];
    if (updatedConfig[configIndex] && updatedConfig[configIndex].fields && updatedConfig[configIndex].fields[fieldIndex]) {
      updatedConfig[configIndex].fields[fieldIndex].options = options;
      setSopFormConfig(updatedConfig);
    }
  };

  /**
   * Fetch view data for task list
   */
  const fetchViewData = async () => {
    const viewData = await getData(getTaskListURL);
    if (taskId) {
      populateFormForEdit(taskId);
    }
  };

  /**
   * Populate form fields for editing existing task
   * @param {string} id - Task ID to edit
   */
  const populateFormForEdit = async (id) => {
    const response = await postDataWithBody(findTaskById, [id], {});
    const taskData = response[0];
    setViewTaskDetails(taskData);
    reset(taskData); // Reset form with existing data
  };

  /**
   * Generate form fields JSX
   * @param {Array} fields - Field configuration array
   * @param {Function} register - React Hook Form register function
   * @param {Object} errors - Form validation errors
   * @param {Object} control - React Hook Form control object
   */
  const generateFormFields = (fields, register, errors, control) => {
    return (
      <div>
        <div
          style={{
            fontSize: "12pt",
            marginLeft: "15px",
            marginTop: "1rem",
            marginBottom: "1rem",
            color: "#1a7a73",
            fontSize: "17px",
            fontWeight: "700px"
          }}
        >
          Basic Information
        </div>
        <FormHandler
          register={register}
          errors={errors}
          fields={fields}
          control={control}
          editId={taskId}
          columns={2}
          getValues={getValues}
          fileObjs={attachedFiles}
          setFileObjs={setAttachedFiles}
          Description={Description}
        />
      </div>
    );
  };

  /**
   * Generate tab content with form components
   * @param {Array} tabData - Tab configuration data
   */
  const generateTabsContent = (tabData) => {
    tabData?.forEach((tab) => {
      tab.component = generateFormFields(
        tab?.fields,
        register,
        errors,
        control
      );
    });
    return tabData;
  };

  /**
   * Convert string priority to numeric value for API
   * @param {string} priority - Priority string ("Low", "Medium", "High")
   * @returns {number} Numeric priority value
   */
  const getPriorityNumericValue = (priority) => {
    switch (priority) {
      case "Low":
        return 0;
      case "Medium":
        return 1;
      case "High":
        return 2;
      default:
        return 0; // Default to Low
    }
  };

  /**
   * Get background color for priority dropdown based on priority level
   * @param {string} priority - Priority level
   * @returns {string} CSS color value
   */
  const getDropdownColor = (priority) => {
    switch (priority) {
      case "Low":
        return "#b2eafe"; // Blue for low priority
      case "Medium":
        return "#fccd7c"; // Yellow for medium priority
      case "High":
        return "#f16a87"; // Red for high priority
      default:
        return "#13845d"; // Default color
    }
  };

  // ========== EVENT HANDLERS ==========

  /**
   * Handle form submission for creating/updating tasks
   * @param {Object} data - Form data
   * @param {Event} e - Form submit event
   */
  const handleFormSubmit = async (data, e) => {
    setIsLoading(true);
    setIsSubmitting(true);
    e.preventDefault();
 
    // Prepare form data with file uploads
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (key === "task_media_file" && value?.length > 0) {
        Array.from(value).forEach((file) => formData.append(key, file));
      } else {
        formData.append(key, value);
      }
    });
 
    formData.append("status", true);
    if (taskId) formData.append("id", taskId);
 
    try {
      const headers = { "Content-Type": "multipart/form-data" };
      // Use appropriate API endpoint based on create/update mode
      const response = taskId
        ? await putDataWithBody(updateTaskURL, formData, headers)
        : await postDataWithBody(createTaskURL, formData, headers);
 
      if (response.status === "success") {
        toast.success(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
        navigate("/sop-management/view-task");
      } else {
        toast.error(response.msg, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error(t("submission_error"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  /**
   * Handle task selection checkbox changes
   */
  const handleTaskSelection = (rowId, isSelected) => {
    setUserTaskData((prevData) =>
      prevData.map((row) => {
        if (row.id === rowId) {
          return { ...row, isSelected };
        }
        return row;
      })
    );
  };

  /**
   * Handle priority dropdown changes
   */
  const handlePriorityChange = (rowId, priority) => {
    // Convert string priority to numeric value for API compatibility
    const priorityValue = getPriorityNumericValue(priority);
 
    setUserTaskData((prevData) =>
      prevData.map((row) => {
        if (row.id === rowId) {
          return { ...row, priority, priorityValue };
        }
        return row;
      })
    );
  };

  /**
   * Execute SOP with selected tasks
   */
  const ExecuteSop = async () => {
    setIsLoading(true);
    setIsSubmitting(true);
 
    try {
      // Get current form data
      const formData = getValues();
 
      // Filter selected tasks
      const selectedTasksList = userTaskData.filter((task) => task.isSelected);
 
      // Validate task selection
      if (selectedTasksList.length === 0) {
        toast.warning(t("Please select at least one task to execute"), {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
        setIsSubmitting(false);
        setIsLoading(false);
        return;
      }
 
      // Create task wrapper list for API
      const taskWrapperList = selectedTasksList.map((task) => ({
        id: task.id.toString(),
        name: task.name,
        type: task.task_type === 0 ? "Activity" : "Notification",
        priority: getPriorityNumericValue(task.priority), // Convert to numeric
        incidentType: task.incidenttype,
        assignedTo: task.assignedto,
        role: task.role || 0,
        taskId: task.id,
        status: "TASK_EXE_STATUS_NEW_TASK",
      }));
 
      // Create resource wrapper list for API
      const resourceWrapperList = sopResources.map((resource) => ({
        id: resource.id.toString(),
        used: resource.used || 0,
        totalUsed: resource.totalUsed || 0,
        assign: 0,
        status: "RESOURCE_EXE_STATUS_NOTUSED",
      }));
 
      // Create API payload
      const payload = {
        incidentId: parseInt(selectedIncidentId),
        sopId: parseInt(selectedSopId),
        name: formData.assign_to_id, // Generated instance name
        description: formData.description,
        userId: keycloak.tokenParsed?.email || "dmsadmin@bihar.gov.in",
        status: "TASK_EXE_STATUS_NEW_TASK",
        executeStatus: true,
        taskWrapperList,
        resourceWrapperList,
      };
 
 
      // Send API request to create SOP instance
      const response = await postDataWithBody(CreateSopInstance, payload, {});
 
      if (response && response.status === "success") {
        toast.success(
          `${response.msg} (SOP ID: ${response.id})` || t("Sop incident created successfully"),
          {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 2000,
          }
        );
        navigate("../Sop-instance-list");
      } else {
        toast.error(response?.msg || t("Failed to execute SOP"), {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.error("Execute SOP error:", error);
      toast.error(t("execution_error") || "Failed to execute SOP", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  /**
   * Handle Execute SOP button click
   */
  const handleExecuteSop = () => {
    ExecuteSop();
  };

  // ========== TABLE CONFIGURATION ==========

  /**
   * Table column configuration for task display
   */
  const tableColumns = React.useMemo(
    () => [
      {
        Header: t("executesopstableheaders.name"),
        accessor: "name",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.tasktype"),
        accessor: "type",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.incidenttype"),
        accessor: "incidenttype",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.assignedto"),
        accessor: "assignedto",
        disableSortBy: true,
      },
      {
        Header: t("executesopstableheaders.priority"),
        accessor: "task_status_code",
        disableSortBy: true,
        Cell: ({ row }) => {
          const rowId = row.original.id;
          const priorityValue = row.original.priority || "Low";
 
          const handleSelectChange = (e) => {
            const newPriority = e.target.value;
            handlePriorityChange(rowId, newPriority);
          };
 
          return (
            <div style={{ position: "relative", width: "120px" }}>
              {/* Priority dropdown with color coding */}
              <select
                value={priorityValue}
                onChange={handleSelectChange}
                style={{
                  padding: "5px",
                  paddingRight: "20px", // Space for custom arrow
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: getDropdownColor(priorityValue),
                  color: "black",
                  width: "100%",
                  textAlign: "center",
                  textAlignLast: "center",
                  appearance: "none", // Remove default browser styling
                  WebkitAppearance: "none"
                }}
              >
                <option value="Low" style={{ textAlign: "center", backgroundColor: "white", color: "black" }}>
                  {t("Low")}
                </option>
                <option value="Medium" style={{ textAlign: "center", backgroundColor: "white", color: "black" }}>
                  {t("Medium")}
                </option>
                <option value="High" style={{ textAlign: "center", backgroundColor: "white", color: "black" }}>
                  {t("High")}
                </option>
              </select>
              {/* Custom dropdown arrow */}
              <div
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  fontSize: "10px",
                  color: "black"
                }}
              >
                ▼
              </div>
            </div>
          );
        },
      },
      {
        Header: t("executesopstableheaders.selecttask"),
        accessor: "isSelected",
        disableSortBy: true,
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.original.isSelected || false}
            onChange={(e) =>
              handleTaskSelection(row.original.id, e.target.checked)
            }
          />
        ),
      },
    ],
    [t, userTaskData]
  );

  // ========== RENDER VARIABLES ==========
  
  // Download URL for task attachments
  const downloadAttachmentURL = `${process.env.REACT_APP_APP_SERVER_ADDRESS}/${process.env.REACT_APP_CORE_CONTEXT_PATH_NAME}/download/download_attachments/?directoryPath=${mediaFilePath}&fileName=Tasks.zip`;
 
  // Generate tab content
  const tabsContent = generateTabsContent(sopFormConfig);

  // ========== COMPONENT RENDER ==========
 
  return (
    <>
      {/* Form Section */}
      <>
        <div className="mt-4">
          {/* Loading indicator */}
          <Loader
            show={isLoading}
            type="body"
            stack="vertical"
            message="Loading Data"
          />
 
          <div className="form-body">
            <div className="registration__form--body">
              {/* Main form for SOP execution */}
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                name="CreateTaskRegForm"
              >
                <div>
                  {/* Render active tab content */}
                  {tabsContent?.map((tab) => (
                    <div key={tab.id}>
                      {tab.id === activeTabIndex && <div>{tab.component}</div>}
                    </div>
                  ))}
                </div>
 
                {/* File attachment download section (only shown when editing) */}
                {taskId && mediaFilePath && (
                  <div className="multi_file_uploaded cursor-pointer">
                    <div
                      onClick={() =>
                        downloadFile(
                          downloadAttachmentURL,
                          `Tasks${Date.now()}.zip`
                        )
                      }
                    >
                      <PaperClipIcon className="cursor-pointer" /> Attached
                      files
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </>

      {/* Task Table Section */}
      <>
        <div>
          {/* Loading indicator for table operations */}
          <Loader show={isLoading} type="body" message="Loading Data" />
 
          {/* Expandable table showing SOP tasks */}
          <ExecuteSopsExpandableTable
            data={userTaskData} // Task data array
            columns={tableColumns} // Column configuration
            numberOfRows={7} // Rows per page
            showExport={false} // Hide export functionality
            showFilter={false} // Hide filter functionality
            showReset={false} // Hide reset functionality
            onExpand={false} // Disable row expansion
            rowCount={rowCount} // Total row count
            headerTableHeadStyle={{ background: "#1a7a73", color: "#FFFFFF" }} // Header styling
            tableStyle={{
              borderRadius: "12px", // Rounded corners
              padding:"0px" // No padding
            }}
          />
        </div>
      </>

      {/* Action Buttons Section */}
      <>
        <div
          className="d-flex"
          style={{ marginTop: "-4.5rem", marginLeft: "10pt" }}
        >
          {/* Execute SOP Button */}
          <Button
            style={{
              fontSize: "10pt",
              paddingLeft: "2rem",
              paddingRight: "2rem",
              marginTop: "80px",
            }}
            onClick={handleExecuteSop} // Handle SOP execution
            type="button"
            className="mx-2"
            variant="primary"
            disabled={isSubmitting || isLoading || !hasSelectedTasks} // Disable if no tasks selected or during operations
          >
            {/* Dynamic button text based on submission state */}
            {isSubmitting ? t("Executing...") : t("Execute SOP")}
          </Button>
        </div>
      </>

      {/* Toast notification container for user feedback */}
      <ToastContainer />
    </>
  );
};
 
export default ExecuteSopsForm;